import React, { useState, useEffect, createContext, useContext, ReactNode } from 'react';
import { Auth0Provider, useAuth0 } from '@auth0/auth0-react';
import { MsalProvider, useMsal, useIsAuthenticated } from '@azure/msal-react';
import { PublicClientApplication, AccountInfo } from '@azure/msal-browser';
import * as microsoftTeams from '@microsoft/teams-js';
import { msalConfig, loginRequest, auth0Config } from './authConfig';

// Type definitions
type IdpType = 'auth0' | 'azure_ad' | 'teams_sso';

interface User {
  email?: string;
  name?: string;
  sub?: string;
}

interface HybridAuthContextType {
  idpType: IdpType;
  login: (email?: string) => void | Promise<void>;
  logout: () => void;
  getAccessToken: () => Promise<string | null | undefined>;
  getAccessTokenSilently: () => Promise<string | null | undefined>;
  user: User | undefined | null;
  isAuthenticated: boolean;
  isLoading: boolean;
}

// Create MSAL instance
const msalInstance = new PublicClientApplication(msalConfig);

// Context for hybrid auth
const HybridAuthContext = createContext<HybridAuthContextType | null>(null);

export const useHybridAuth = (): HybridAuthContextType => {
  const context = useContext(HybridAuthContext);
  if (!context) {
    throw new Error('useHybridAuth must be used within HybridAuthProvider');
  }
  return context;
};

// Wrapper for Auth0 auth hook
interface Auth0WrapperProps {
  children: ReactNode;
}

const Auth0Wrapper: React.FC<Auth0WrapperProps> = ({ children }) => {
  const auth0 = useAuth0();
  const { loginWithRedirect, logout, getAccessTokenSilently, user, isAuthenticated, isLoading } = auth0;

  const contextValue: HybridAuthContextType = {
    idpType: 'auth0',
    login: (email?: string) => loginWithRedirect(),
    logout: () => logout({ logoutParams: { returnTo: window.location.origin } }),
    getAccessToken: getAccessTokenSilently,
    getAccessTokenSilently,
    user,
    isAuthenticated,
    isLoading,
  };

  return <HybridAuthContext.Provider value={contextValue}>{children}</HybridAuthContext.Provider>;
};

// Wrapper for Azure AD auth hook
const MsalWrapper: React.FC<Auth0WrapperProps> = ({ children }) => {
  const { instance, accounts } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const [isLoading, setIsLoading] = useState(false);

  // Handle redirect response on mount
  useEffect(() => {
    const handleRedirect = async () => {
      try {
        const response = await instance.handleRedirectPromise();
        if (response) {
          console.log('Microsoft login successful', response);
          // Clear the selected IDP flag after successful login
          localStorage.removeItem('selectedIdpType');
          localStorage.removeItem('shouldAutoLogin');
        }
      } catch (error) {
        console.error('Error handling redirect:', error);
      }
    };

    handleRedirect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const user: User | null = accounts[0]
    ? {
        email: accounts[0].username,
        name: accounts[0].name,
        sub: accounts[0].localAccountId,
      }
    : null;

  const login = async (email?: string) => {
    setIsLoading(true);
    try {
      await instance.loginRedirect({
        ...loginRequest,
        loginHint: email,
      });
    } catch (error) {
      console.error('Azure AD login failed:', error);
      setIsLoading(false);
    }
  };

  // Auto-trigger login if flag is set
  useEffect(() => {
    const triggerAutoLogin = (window as any).triggerAutoLogin;
    if (triggerAutoLogin && !isAuthenticated) {
      console.log('Auto-triggering Azure AD login');
      delete (window as any).triggerAutoLogin;
      login();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  const logout = () => {
    instance.logoutRedirect({
      postLogoutRedirectUri: window.location.origin,
    });
  };

  const getAccessToken = async (): Promise<string | null> => {
    if (accounts.length === 0) return null;

    try {
      const response = await instance.acquireTokenSilent({
        ...loginRequest,
        account: accounts[0],
      });
      // Return ID token instead of access token
      // ID token has our app's client ID as audience
      return response.idToken;
    } catch (error) {
      console.error('Token acquisition failed:', error);
      // Try interactive login
      const response = await instance.acquireTokenPopup(loginRequest);
      return response.idToken;
    }
  };

  const contextValue: HybridAuthContextType = {
    idpType: 'azure_ad',
    login,
    logout,
    getAccessToken,
    getAccessTokenSilently: getAccessToken,
    user,
    isAuthenticated,
    isLoading,
  };

  return <HybridAuthContext.Provider value={contextValue}>{children}</HybridAuthContext.Provider>;
};

// Wrapper for Teams SSO auth
interface TeamsSsoWrapperProps {
  children: ReactNode;
  initialToken: string;
  teamsContext: microsoftTeams.app.Context;
}

const TeamsSsoWrapper: React.FC<TeamsSsoWrapperProps> = ({ children, initialToken, teamsContext }) => {
  const [token, setToken] = useState<string>(initialToken);
  const [isLoading, setIsLoading] = useState(false);

  // Extract user info from Teams context
  const user: User | null = teamsContext?.user ? {
    email: teamsContext.user.userPrincipalName,
    name: teamsContext.user.displayName,
    sub: teamsContext.user.id,
  } : null;

  // Refresh token if needed (Teams tokens expire)
  const getAccessToken = async (): Promise<string | null> => {
    try {
      // Always get a fresh token from Teams SDK
      const freshToken = await microsoftTeams.authentication.getAuthToken();
      setToken(freshToken);
      return freshToken;
    } catch (error) {
      console.error('Teams SSO token refresh failed:', error);
      // Return cached token as fallback
      return token;
    }
  };

  const contextValue: HybridAuthContextType = {
    idpType: 'teams_sso',
    login: () => {
      // No-op in Teams - SSO is automatic
      console.log('Login not needed in Teams SSO context');
    },
    logout: () => {
      // No-op in Teams - can't log out of Teams from within
      console.log('Logout not available in Teams SSO context');
    },
    getAccessToken,
    getAccessTokenSilently: getAccessToken,
    user,
    isAuthenticated: !!token,
    isLoading,
  };

  return <HybridAuthContext.Provider value={contextValue}>{children}</HybridAuthContext.Provider>;
};

// Main Hybrid Auth Provider
interface HybridAuthProviderProps {
  children: ReactNode;
}

export const HybridAuthProvider: React.FC<HybridAuthProviderProps> = ({ children }) => {
  const [idpType, setIdpType] = useState<IdpType | null>(null);
  const [msalInitialized, setMsalInitialized] = useState(false);
  const [teamsToken, setTeamsToken] = useState<string | null>(null);
  const [teamsContext, setTeamsContext] = useState<microsoftTeams.app.Context | null>(null);

  // Initialize MSAL instance
  useEffect(() => {
    const initMsal = async () => {
      await msalInstance.initialize();
      setMsalInitialized(true);
    };
    initMsal();
  }, []);

  // Check if user already has an active session or selected IDP
  useEffect(() => {
    if (!msalInitialized) return; // Wait for MSAL to initialize

    const checkExistingSession = async () => {
      // FIRST: Try Teams SDK - this takes priority if we're in Teams iframe
      try {
        await microsoftTeams.app.initialize();
        const context = await microsoftTeams.app.getContext();
        console.log('Running in Teams context:', context.app?.host?.name);

        // Get SSO token from Teams
        const token = await microsoftTeams.authentication.getAuthToken();
        console.log('Teams SSO token acquired');

        setTeamsContext(context);
        setTeamsToken(token);
        setIdpType('teams_sso');
        return;
      } catch (teamsError) {
        // Not in Teams or Teams SDK failed - continue to other IDPs
        console.log('Not in Teams context, checking other IDPs');
      }

      // Check if user selected a specific IDP (from login button)
      const selectedIdp = localStorage.getItem('selectedIdpType') as IdpType | null;
      const shouldAutoLogin = localStorage.getItem('shouldAutoLogin');

      if (selectedIdp && selectedIdp !== 'teams_sso') {
        console.log('Using selected IDP from localStorage:', selectedIdp);
        setIdpType(selectedIdp);

        // If we just switched IDP, trigger auto-login after provider mounts
        if (shouldAutoLogin === 'true') {
          localStorage.removeItem('shouldAutoLogin');
          // Auto-login will be triggered by the wrapper components
          (window as any).triggerAutoLogin = true;
        }
        return;
      }

      // Check Auth0 session
      const auth0Session = localStorage.getItem(
        `@@auth0spajs@@::${auth0Config.clientId}::${auth0Config.audience}::openid profile email`
      );
      if (auth0Session) {
        setIdpType('auth0');
        return;
      }

      // Check MSAL session
      const msalAccounts = msalInstance.getAllAccounts();
      if (msalAccounts.length > 0) {
        setIdpType('azure_ad');
        return;
      }

      // No active session - default to Auth0
      setIdpType('auth0');
    };

    checkExistingSession();
  }, [msalInitialized]);

  // Function to switch IDP (called from login page buttons)
  const switchToIDP = (targetIdp: IdpType) => {
    if (targetIdp !== idpType) {
      console.log('Switching IDP from', idpType, 'to', targetIdp);
      localStorage.setItem('selectedIdpType', targetIdp);
      localStorage.setItem('shouldAutoLogin', 'true');
      // Force page reload to reinitialize with correct provider
      window.location.reload();
    }
  };

  // Expose IDP switch function globally for login page
  useEffect(() => {
    (window as any).switchToIDP = switchToIDP;
    return () => {
      delete (window as any).switchToIDP;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idpType]);

  // Loading state while determining IDP
  if (idpType === null) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <div>Loading authentication...</div>
      </div>
    );
  }

  // Render appropriate provider based on IDP type
  if (idpType === 'teams_sso' && teamsToken && teamsContext) {
    return (
      <TeamsSsoWrapper initialToken={teamsToken} teamsContext={teamsContext}>
        {children}
      </TeamsSsoWrapper>
    );
  }

  if (idpType === 'azure_ad') {
    return (
      <MsalProvider instance={msalInstance}>
        <MsalWrapper>{children}</MsalWrapper>
      </MsalProvider>
    );
  }

  // Default to Auth0
  return (
    <Auth0Provider
      domain={auth0Config.domain}
      clientId={auth0Config.clientId}
      authorizationParams={{
        redirect_uri: auth0Config.redirectUri,
        audience: auth0Config.audience,
        scope: 'openid profile email',
      }}
      cacheLocation="localstorage"
      useRefreshTokens={true}
    >
      <Auth0Wrapper>{children}</Auth0Wrapper>
    </Auth0Provider>
  );
};
